import React, { ReactElement, useContext, useState } from 'react';
import { GlobalContext } from '../../pages/_app';
// import SignUpPhoto from './signup/SignUpPhoto';
import {
    getTranslation,
    getTranslationKey,
    useTranslation,
} from 'utils/localization';
import SignUpPersonalDetails from 'components/login/signup/SignUpPersonalDetails';
import SignUpLogInDetails from 'components/login/signup/SignUpLogInDetails';
import SignUpActivation from 'components/login/signup/SignUpActivation';
import AppLayout from 'components/layouts/AppLayout';
import {
    BasicCountryEnum,
    ValuesStringInterface,
} from 'components/interfaces/GeneralInterface';
import SignUpInitialDetails from 'components/login/signup/SignUpInitialDetails';
import { isAbove15 } from 'utils/index';
import {
    MemberRegistrationApiInterface,
    RepresentativeRelationEnum,
} from 'submodules/api_middleware';
import { useLanguagesList } from 'utils/customHooks/useLanguagesList';
import { useCountriesList } from 'utils/customHooks/useCountriesList';
import EditFormBreadcrumbs from 'components/editForm/EditFormBreadcrumbs';
import { getDiscipline } from 'utils/helpers/browser/discipline';
import { EditFormDataInterface } from 'components/interfaces/EditFormInterface';
import { TNamespaceEnum } from 'types/types';
import { useExistingAddress } from 'utils/customHooks/useExistingAddress';
import styles from './SignUpPage.module.scss';

const KEYS = {
    SignUpInitialDetails: 'SignUpInitialDetails',
    SignUpPersonalDetails: 'SignUpPersonalDetails',
    // TODO: IT-677 - enable
    // SignUpPhoto: 'SignUpPhoto',
    SignUpLogInDetails: 'SignUpLogInDetails',
    SignUpActivation: 'SignUpActivation',
};

const SignUpPage = (): ReactElement => {
    const { t } = useTranslation();

    const [values, setValues] = useState<ValuesStringInterface>({});
    const [currentIdx, setCurrentIdx] = useState(0);

    const languagesList = useLanguagesList();
    const countriesList = useCountriesList();

    // Conversion function of form values to an interface
    const getRequestData = (): MemberRegistrationApiInterface => ({
        discipline: getDiscipline(),
        user: values.password
            ? {
                  password: values.password,
              }
            : undefined,
        person: {
            birth_country_code: values.birth_country_code,
            birth_date: new Date(values.birth_year).toISOString(),
            email: values.email,
            first_name: values.first_name,
            middle_name: values.middle_name,
            last_name: values.last_name,
            gender: values.gender === 'male' ? 'male' : 'female',
            // the form uses a shared field for national id and passport, whereas BE expects the id and passport in separated fields.
            national_identifier: values.national_identifier ?? '',
            passport:
                values.nationality !== BasicCountryEnum.CZ
                    ? values.passport
                    : '',
            phone:
                !values.phone ||
                values.phone == '' ||
                values.phone?.trim() === '+420'
                    ? null
                    : values.phone,
            title: values.title,
            citizen_of: values.nationality,
            language: values.register_as_player ? values.language : undefined,
            birth_city: values.register_as_player
                ? values.birthCity
                : undefined,
            second_citizen_of: values.register_as_player
                ? values.nationality2
                : undefined,
            marketing_agreement: !!values.marketing_agreement,
            targito_agreement: !!values.targito_agreement,
        },
        register_through_club_id: !values.club_id
            ? null
            : parseInt(values.club_id),
        copy_permanent_address: !!values.copy_permanent_address,
        address: {
            country_code: values.country_address,
            postal_code: values.postal_code,
            street: values.street,
            street_number: values.street_number,
            city: values.city,
        },
        representative: isMinor
            ? {
                  first_name: values.representative_first_name,
                  last_name: values.representative_last_name,
              }
            : null,
        representative_relation: isMinor
            ? (values.representative_relation as RepresentativeRelationEnum)
            : null,
    });

    const goNext = () => setCurrentIdx(currentIdx + 1);

    const goBack = () => setCurrentIdx(currentIdx - 1);

    const getFormData = (data: EditFormDataInterface) => {
        setValues({ ...values, ...data.state });
    };

    const { user } = useContext(GlobalContext);
    const isSignedIn = !!user;
    const adultExists: boolean =
        isSignedIn &&
        user.user_persons.some(
            (x: { registered_as_minor: boolean }) =>
                x.registered_as_minor === false
        );

    const isMinor = !isAbove15(
        new Date(values.birth_year ? values.birth_year : null)
    );

    // We want to load an existing address of previous minor or of a registered adult,
    // but only when there is a user signed in, and only when we are registering a minor.
    const existingAddress = useExistingAddress(isSignedIn && isMinor);

    const logInDetailsTitleKey = isMinor
        ? getTranslationKey(TNamespaceEnum.signup, 'representativeDetails')
        : getTranslationKey(TNamespaceEnum.signup, 'logInDetails');

    const breadCrumb = [
        'pages.basicDetails',
        'pages.personDetails',
        // TODO: IT-677 - enable
        // ...(values.register_as_player ? ['pages.photoUpload'] : []),
        ...(!isSignedIn
            ? [
                  logInDetailsTitleKey,
                  getTranslationKey(TNamespaceEnum.signup, 'accountActivation'),
              ]
            : []),
    ];

    /**
     * There are 4 basic workflows, based on `isSignedIn` and `isMinor` flags,
     * along with a branch for `register_as_player`.
     */
    const components: ReactElement[] = [
        <SignUpInitialDetails
            goNext={goNext}
            mustBeMinor={adultExists}
            values={values}
            countriesList={countriesList}
            key={KEYS.SignUpInitialDetails}
            getFormData={getFormData}
        />,
        <SignUpPersonalDetails
            values={values}
            validateCzechPhoneFormat={
                values.nationality === BasicCountryEnum.CZ ||
                (values.national_identifier &&
                    values.national_identifier !== '')
            }
            shouldRegister={isSignedIn}
            registrationModeInfoKey={
                !isSignedIn
                    ? null
                    : isMinor
                    ? 'registrationModeInfoAdditionalMinor'
                    : 'registrationModeInfoAdditionalAdult'
            }
            existingAddress={existingAddress}
            birthdate={values['birth_year']}
            nationality={values['nationality']}
            goNext={goNext}
            goBack={goBack}
            countriesList={countriesList}
            languagesList={languagesList}
            key={KEYS.SignUpPersonalDetails}
            getFormData={getFormData}
            getRequestData={getRequestData}
            // the representative relation field is hidden on the personal details page depending on
            // whether the user is logged in or not. The registering user should be a minor
            isMinor={isSignedIn && isMinor}
        />,
        /**
         * Players must be verified before the match, so we need actual photo
         * to check the identity.
         * // TODO: IT-677 - enable the block below
         */
        /* ...(values.register_as_player
            ? [
                  <SignUpPhoto
                      goNext={goNext}
                      values={values}
                      goBack={goBack}
                      key={KEYS.SignUpPhoto}
                      getFormData={getFormData}
                  />,
              ]
            : []), */
        /**
         * If user is already signed in, we know the credentials and have email.
         * No need to ask again.
         */
        ...(!isSignedIn
            ? [
                  <SignUpLogInDetails
                      getRequestData={getRequestData}
                      isMinor={isMinor}
                      titleKey={logInDetailsTitleKey}
                      values={values}
                      validateCzechPhoneFormat={values.nationality === 'CZ'}
                      hidePassword={isMinor}
                      goNext={goNext}
                      goBack={goBack}
                      key={KEYS.SignUpLogInDetails}
                      getFormData={getFormData}
                  />,
                  <SignUpActivation
                      getRequestData={getRequestData}
                      photo={
                          // TODO: IT-677 - enable
                          // values.register_as_player ? values.photo : undefined
                          undefined
                      }
                      goBack={goBack}
                      key={KEYS.SignUpActivation}
                  />,
              ]
            : []),
    ];

    return (
        <AppLayout
            title={getTranslation(t, TNamespaceEnum.signup, 'pageTitle')}
            pageTitle={{
                primaryBoldText: getTranslation(
                    t,
                    TNamespaceEnum.signup,
                    'signUp'
                ),
                isDark: true,
            }}
            hideUserMenu
            showDisciplineMenu
            hideLoginAs
            changeDisciplineWithoutReload={true}
            showLangMenu={true}
        >
            <div>
                <EditFormBreadcrumbs
                    step={currentIdx}
                    setStep={setCurrentIdx}
                    breadcrumbs={breadCrumb}
                />
                <div className={styles.formWrapper}>
                    {components[parseInt(`${currentIdx}`)]}
                </div>
            </div>
        </AppLayout>
    );
};

export default SignUpPage;
